import React, { useState, useEffect, useRef } from 'react';
import Scene from './components/Gallery/Scene';
import SideMenu from './components/UI/SideMenu';
import ImageModal from './components/UI/ImageModal';
import ControlBar from './components/UI/ControlBar';
import ShareModal from './components/UI/ShareModal';
import RightMenu from './components/UI/RightMenu.jsx';
import GalleryHeader from './components/UI/GalleryHeader';
import WalletButton from './components/UI/WalletButton';
import ConnectOverlay from './components/UI/ConnectOverlay';
import StartDashboard from './components/UI/StartDashboard';
import ErrorBoundary from './components/UI/ErrorBoundary';
import LoadingIndicator from './components/UI/LoadingIndicator';
import PerformanceMonitor from './components/UI/PerformanceMonitor';
import OnboardingTour from './components/UI/OnboardingTour';
import EmptyGalleryGuide from './components/UI/EmptyGalleryGuide';
import NFTTabTooltip from './components/UI/NFTTabTooltip';
import Footer from './components/UI/Footer';
import BrowserCompatibility from './components/UI/BrowserCompatibility';
import FAQ from './components/Help/FAQ';
import { HederaProvider, useHedera } from './contexts/HederaContext';
import { TransformProvider } from './contexts/TransformContext';
import { galleries as galleriesApi } from './services/api';
import html2canvas from 'html2canvas';
import './App.css';
import './overlay.css';
import LoadingSketch from './components/LoadingSketch';

// Replace the TestComponent with a proper debug system
const DEBUG_MODE = process.env.REACT_APP_DEBUG === 'true';

function DebugPanel({ state }) {
  if (!DEBUG_MODE) return null;

  return (
    <div className="debug-panel">
      <pre>{JSON.stringify(state, null, 2)}</pre>
    </div>
  );
}

// Main app content component
function AppContent() {
  // console.log('AppContent rendering');
  const { accountId, isInitializing } = useHedera();
  const sceneRef = useRef();
  const [fov, setFov] = useState(60);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRightMenuOpen, setIsRightMenuOpen] = useState(false);
  const [assets, setAssets] = useState({});
  const [modalAsset, setModalAsset] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [galleryImage, setGalleryImage] = useState(null);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [currentGallery, setCurrentGallery] = useState(null);
  const [loading, setLoading] = useState({
    initialization: true,
    galleries: false,
    general: false,
    wallet: true
  });
  const [isViewMode, setIsViewMode] = useState(true);  // Default to view mode
  const [showStartDashboard, setShowStartDashboard] = useState(false);
  const [error, setError] = useState(null);
  const [showLoading, setShowLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showPerformanceMonitor, setShowPerformanceMonitor] = useState(DEBUG_MODE);
  const [isNewGallery, setIsNewGallery] = useState(false);
  const [showEmptyGalleryGuide, setShowEmptyGalleryGuide] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Debug info
  // console.log('App state:', {
  //   showLoading,
  //   isInitializing,
  //   isViewMode,
  //   accountId
  // });

  // Check URL for view mode parameter and load shared gallery immediately
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewMode = params.get('view') === 'true';
    const gallerySlug = params.get('gallery');

    setIsViewMode(viewMode);

    if (gallerySlug) {
      loadSharedGallery(gallerySlug);
    }
  }, []);

  // Load user's galleries when wallet connects (separate from shared gallery loading)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gallerySlug = params.get('gallery');
    
    if (accountId) {
      // Only load user galleries if we're not viewing a shared gallery
      if (!gallerySlug) {
        loadUserGalleries();
      }
      setLoading(prev => ({ ...prev, wallet: false }));
    } else {
      // Clear user-specific data when wallet disconnects
      setGalleries([]);
      // Don't clear currentGallery if we're viewing a shared gallery
      if (!gallerySlug) {
        setCurrentGallery(null);
        setAssets({});
      }
      setShowStartDashboard(false);
    }
  }, [accountId]);

  // Add useEffect for initialization
  useEffect(() => {
    // Set initialization to false once component is mounted
    setLoading(prev => ({ ...prev, initialization: false }));
  }, []);

  const handleError = (error, context) => {
    console.error(`Error in ${context}:`, error);
    setError({ message: error.message, context });
    // Could add a toast notification here
  };

  const loadUserGalleries = async () => {
    try {
      setLoading({ ...loading, galleries: true });
      const userGalleries = await galleriesApi.getUserGalleries(accountId);
      setGalleries(userGalleries);

      // Commenting out automatic loading of first gallery
      // if (userGalleries.length > 0 && !currentGallery) {
      //   const firstGallery = await galleriesApi.getGalleryBySlug(userGalleries[0].slug);
      //   setCurrentGallery(firstGallery);
      //   setAssets(firstGallery.assets || {});
      // }
    } catch (error) {
      handleError(error, 'loading galleries');
    } finally {
      setLoading({ ...loading, galleries: false });
    }
  };

  const loadSharedGallery = async (slug) => {
    try {
      setLoading({ ...loading, general: true });
      const gallery = await galleriesApi.getGalleryBySlug(slug, accountId);
      console.log('Loaded gallery:', gallery);
      setCurrentGallery(gallery);
      setAssets(gallery.assets || {});

      // If it's a public gallery and we're not the owner, set view mode
      // Also set view mode if no wallet is connected (anonymous viewing)
      if (gallery.isPublic && (!accountId || gallery.owner !== accountId)) {
        setIsViewMode(true);
        setShowStartDashboard(false);
      }
      
      // For anonymous users viewing shared galleries, always set view mode
      if (!accountId) {
        setIsViewMode(true);
        setShowStartDashboard(false);
      }
    } catch (error) {
      handleError(error, 'loading shared gallery');
    } finally {
      setLoading({ ...loading, general: false });
    }
  };

  const handleSpotSelect = (spotId, fromList = false, isNFTInfo = false) => {
    if (isViewMode && assets[spotId]?.url) {
      // When in view mode and spot has an asset, show the modal
      setModalAsset({
        ...assets[spotId],
        isViewing: true
      });
      return;
    }

    if (isNFTInfo && assets[spotId]) {
      // When viewing NFT info, show modal with isViewing flag
      setModalAsset({
        ...assets[spotId],
        isViewing: true
      });
      return;
    }

    setSelectedSpot(spotId);
    setIsMenuOpen(true);
  };

  const handleMenuClose = () => {
    // Save the current spot's state before closing
    if (selectedSpot && assets[selectedSpot]) {
      const currentAsset = assets[selectedSpot];
      if (currentAsset.position || currentAsset.rotation || currentAsset.scale) {
        handleAssetAdd(selectedSpot, currentAsset);
      }
    }
    setIsMenuOpen(false);
    setSelectedSpot(null);
  };

  const handleAssetAdd = async (spotId, asset, onAssetsUpdate) => {
    if (isViewMode) return;

    // Keep track of the previous state for rollback first
    const previousAssets = { ...assets };

    // Handle deletion case (when asset is null)
    if (asset === null) {
      // Create new assets object without the deleted spot
      const updatedAssets = Object.fromEntries(
        Object.entries(previousAssets).filter(([id]) => id !== spotId)
      );

      // Optimistic update
      setAssets(updatedAssets);

      try {
        // Send the updated state to the server
        const response = await galleriesApi.updateAssets(currentGallery._id, updatedAssets, accountId);
        
        if (response.assets) {
          setAssets(response.assets);
          onAssetsUpdate?.(response.assets);
        }
      } catch (error) {
        console.error('Asset deletion failed:', error);
        setAssets(previousAssets); // Rollback on error
        handleError(error, 'deleting asset');
      }
      return;
    }

    // Handle asset addition/update
    console.log('Starting asset addition - Full transform details:', {
      spotId,
      newAsset: {
        position: asset?.position,
        rotation: asset?.rotation,
        scale: asset?.scale,
        fullAsset: asset
      },
      previousAssetState: {
        position: previousAssets[spotId]?.position,
        rotation: previousAssets[spotId]?.rotation,
        scale: previousAssets[spotId]?.scale,
        fullAsset: previousAssets[spotId]
      },
      currentAssets: assets
    });

    const updatedAssets = {
      ...previousAssets,
      [spotId]: {
        ...asset,
        // Preserve existing transform if it exists
        position: previousAssets[spotId]?.position || asset.position || [0, 0, 0],
        rotation: previousAssets[spotId]?.rotation || asset.rotation || [0, 0, 0],
        scale: previousAssets[spotId]?.scale || asset.scale || (asset.isModel ? [0.5, 0.5, 0.5] : [1, 1, 1])
      }
    };

    // Optimistic update
    setAssets(updatedAssets);

    try {
      // Send the updated state to the server
      const response = await galleriesApi.updateAssets(currentGallery._id, updatedAssets, accountId);
      
      if (response.assets) {
        // Preserve local transforms for the selected spot
        const finalAssets = selectedSpot ? {
          ...response.assets,
          [selectedSpot]: {
            ...response.assets[selectedSpot],
            position: updatedAssets[selectedSpot]?.position || response.assets[selectedSpot]?.position,
            rotation: updatedAssets[selectedSpot]?.rotation || response.assets[selectedSpot]?.rotation,
            scale: updatedAssets[selectedSpot]?.scale || response.assets[selectedSpot]?.scale
          }
        } : response.assets;

        setAssets(finalAssets);
        onAssetsUpdate?.(finalAssets);
      }
    } catch (error) {
      console.error('Asset addition failed:', error);
      setAssets(previousAssets); // Rollback on error
      handleError(error, 'adding asset');
    }
  };

  const handleCreateGallery = async (name, roomType = 'classic', glbPath = null) => {
    if (!accountId) {
      alert('Please connect your wallet first');
      return;
    }

    console.log('=== APP HANDLE CREATE GALLERY DEBUG ===');
    console.log('Parameters received:');
    console.log('  name:', name);
    console.log('  roomType:', roomType);
    console.log('  glbPath:', glbPath);
    console.log('  typeof roomType:', typeof roomType);

    // If roomType is custom-glb but no glbPath is provided, use the template
    if (roomType === 'custom-glb' && !glbPath) {
      glbPath = '/rooms/RoomTemplate.glb';
      console.log('Using default GLB template:', glbPath);
    }

    try {
      const galleryData = {
        name,
        theme: 'default',
        roomType: roomType,
        glbPath: glbPath
      };
      
      console.log('Gallery data object created:', JSON.stringify(galleryData, null, 2));
      
      const newGallery = await galleriesApi.create(galleryData, accountId);

      console.log('New gallery received from API:', newGallery);
      console.log(`Gallery created successfully with roomType: ${newGallery.roomType}`);
      
      // If the roomType is missing in the response, add it manually as a workaround
      if (!newGallery.roomType && roomType) {
        console.log(`Adding missing roomType ${roomType} to gallery object`);
        newGallery.roomType = roomType;
      }
      
      // If the glbPath is missing in the response, add it manually as a workaround
      if (!newGallery.glbPath && glbPath) {
        console.log(`Adding missing glbPath ${glbPath} to gallery object`);
        newGallery.glbPath = glbPath;
      }
      
      console.log('Final gallery object:', {
        roomType: newGallery.roomType,
        glbPath: newGallery.glbPath
      });
      
      setGalleries(prev => [...prev, newGallery]);
      setCurrentGallery(newGallery);
      setAssets(newGallery.assets || {});
      setIsNewGallery(true);
      return newGallery;
    } catch (error) {
      console.error('Failed to create gallery:', error);
      alert('Failed to create gallery');
      throw error;
    }
  };

  const handleGallerySelect = async (gallery) => {
    try {
      setLoading({ ...loading, general: true });
      const completeGallery = await galleriesApi.getGalleryBySlug(gallery.slug);
      console.log('Setting current gallery:', completeGallery);
      console.log('Selected gallery details:', {
        roomType: completeGallery.roomType,
        glbPath: completeGallery.glbPath
      });
      
      // Make sure we have a glbPath if this is a custom GLB room
      if (completeGallery.roomType === 'custom-glb' && !completeGallery.glbPath) {
        console.warn('Gallery has custom-glb roomType but no glbPath in client');
        completeGallery.glbPath = '/rooms/RoomTemplate.glb';
      }
      
      setCurrentGallery(completeGallery);
      setAssets(completeGallery.assets || {});
      setIsNewGallery(false);
    } catch (error) {
      console.error('Failed to load gallery:', error);
      alert('Failed to load gallery');
    } finally {
      setLoading({ ...loading, general: false });
    }
  };

  const takeScreenshot = async () => {
    if (isCapturingScreenshot) return;
    
    try {
      setIsCapturingScreenshot(true);
      
      const renderer = sceneRef.current?.getRenderer();
      const scene = sceneRef.current?.getScene();
      const camera = sceneRef.current?.getCamera();
      
      if (renderer && scene && camera) {
        // Wait for next frame to ensure scene is fully rendered
        await new Promise(resolve => requestAnimationFrame(resolve));
        
        // Force a render with the current scene and camera
        renderer.render(scene, camera);
        
        // Wait another frame to ensure render is complete
        await new Promise(resolve => requestAnimationFrame(resolve));
        
        // Get the image data
        const screenshot = renderer.domElement.toDataURL('image/png', 1.0);
        
        // Create download link and trigger download immediately
        const link = document.createElement('a');
        link.download = `gallery-screenshot-${Date.now()}.png`;
        link.href = screenshot;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Also store for potential share usage
        setGalleryImage(screenshot);
        return screenshot;
      } else {
        console.error('Scene, camera, or renderer not initialized', {
          hasRenderer: !!renderer,
          hasScene: !!scene,
          hasCamera: !!camera
        });
        throw new Error('Scene not ready for screenshot');
      }
    } catch (error) {
      console.error('Error taking screenshot:', error);
      throw error;
    } finally {
      setIsCapturingScreenshot(false);
    }
  };

  const handleShare = async () => {
    try {
      if (!galleryImage) {
        await takeScreenshot();
      }
      setIsShareModalOpen(true);
    } catch (error) {
      console.error('Error preparing share:', error);
      // Open modal anyway to allow URL sharing even if screenshot fails
      setIsShareModalOpen(true);
    }
  };

  const toggleViewMode = () => {
    const newViewMode = !isViewMode;
    setIsViewMode(newViewMode);

    // Update URL without reloading the page
    const url = new URL(window.location);
    if (newViewMode) {
      url.searchParams.set('view', 'true');
    } else {
      url.searchParams.delete('view');
    }
    window.history.pushState({}, '', url);

    // Close menus in view mode
    if (newViewMode) {
      setIsMenuOpen(false);
      setIsRightMenuOpen(false);
      setSelectedSpot(null);
    }
  };

  const handleFadeComplete = () => {
    setShowLoading(false);
  };

  // Handler for FOV changes
  const handleFovChange = (newFov) => {
    setFov(newFov);
    // The Scene component exposes setFov through useImperativeHandle
    if (sceneRef.current?.setFov) {
      sceneRef.current.setFov(newFov);
    }
  };

  const handleDeleteGallery = async (galleryId) => {
    try {
      setLoading({ ...loading, general: true });
      await galleriesApi.delete(galleryId, accountId);
      
      // Remove the gallery from the list
      setGalleries(prev => prev.filter(g => g._id !== galleryId));
      
      // If the deleted gallery was the current one, load another gallery or clear current
      if (currentGallery?._id === galleryId) {
        const remainingGalleries = galleries.filter(g => g._id !== galleryId);
        if (remainingGalleries.length > 0) {
          const firstGallery = await galleriesApi.getGalleryBySlug(remainingGalleries[0].slug);
          setCurrentGallery(firstGallery);
          setAssets(firstGallery.assets || {});
        } else {
          setCurrentGallery(null);
          setAssets({});
        }
      }
    } catch (error) {
      console.error('Failed to delete gallery:', error);
      alert('Failed to delete gallery');
    } finally {
      setLoading({ ...loading, general: false });
    }
  };

  return (
    <ErrorBoundary>
      <div className="App" style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
        <DebugPanel state={{
          loading: loading.general,
          isInitializing,
          isViewMode,
          accountId
        }} />

        <LoadingSketch
          isLoading={isInitializing}
          onFadeComplete={handleFadeComplete}
        />
        
        <PerformanceMonitor enabled={showPerformanceMonitor} />
      {currentGallery ? (
        <>
          {/* console.log('📱 App: Rendering Scene with galleryId:', currentGallery._id) */}
          <Scene
            ref={sceneRef}
            onSpotSelect={handleSpotSelect}
            assets={assets}
            isViewMode={isViewMode}
            galleryId={currentGallery?._id}
            isRightMenuOpen={isRightMenuOpen}
            initialFov={fov}
            onAssetsUpdate={setAssets}
            currentGallery={currentGallery}
            glbPath={currentGallery?.glbPath}
          />
        </>
      ) : (
        <>
          {/* console.log('📱 App: NO currentGallery - Scene will show Welcome') */}
          {accountId && (
            <Scene
              ref={sceneRef}
              onSpotSelect={handleSpotSelect}
              assets={assets}
              isViewMode={isViewMode}
              galleryId={null}
              isRightMenuOpen={isRightMenuOpen}
              initialFov={fov}
              onAssetsUpdate={setAssets}
              currentGallery={null}
              glbPath={null}
            />
          )}
        </>
      )}

      {!isViewMode && <WalletButton />}
      {!isViewMode && !currentGallery && <ConnectOverlay setShowStartDashboard={setShowStartDashboard} />}
      {(showStartDashboard === true || showStartDashboard?.show) && !isViewMode && (
        <StartDashboard
          onCreateGallery={handleCreateGallery}
          onDeleteGallery={handleDeleteGallery}
          onClose={() => setShowStartDashboard(false)}
          galleries={galleries}
          isLoading={loading.general}
          onGallerySelect={handleGallerySelect}
          showBackToGallery={showStartDashboard?.fromGallery}
          currentGalleryName={currentGallery?.name}
        />
      )}
      {currentGallery && !isViewMode && (
        <SideMenu
          isOpen={isMenuOpen || selectedSpot !== null}
          onClose={handleMenuClose}
          selectedSpot={selectedSpot}
          onAssetAdd={handleAssetAdd}
          assets={assets}
          onAssetsUpdate={setAssets}
          isViewMode={isViewMode}
          isNewGallery={isNewGallery}
          onNewGalleryViewed={() => {
            setIsNewGallery(false);
            setShowEmptyGalleryGuide(true);
          }}
        />
      )}
      <ImageModal
        asset={modalAsset}
        onClose={() => setModalAsset(null)}
      />
      {currentGallery && (
        <>
          <GalleryHeader galleryName={currentGallery?.name} />
          <ControlBar
            onScreenshot={takeScreenshot}
            onShare={handleShare}
            onMenuToggle={() => setIsRightMenuOpen(!isRightMenuOpen)}
            galleryName={currentGallery?.name}
            isViewMode={isViewMode}
            onViewModeToggle={toggleViewMode}
            isOwner={accountId && currentGallery?.owner === accountId}
            onHelp={() => setShowHelp(true)}
          />
        </>
      )}
      {currentGallery && (
        <RightMenu
          isOpen={isRightMenuOpen}
          onClose={() => setIsRightMenuOpen(false)}
          galleries={galleries}
          currentGallery={currentGallery}
          onGallerySelect={handleGallerySelect}
          onCreateGallery={handleCreateGallery}
          isLoading={loading.general}
          fov={fov}
          setFov={handleFovChange}
          setShowStartDashboard={setShowStartDashboard}
        />
      )}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        galleryImage={galleryImage}
        gallerySlug={currentGallery?.slug}
        isCapturingScreenshot={isCapturingScreenshot}
      />
      
      <OnboardingTour
        isVisible={showOnboarding}
        onComplete={() => setShowOnboarding(false)}
      />
      
      <EmptyGalleryGuide
        isVisible={showEmptyGalleryGuide}
        isViewMode={isViewMode}
        assetsCount={assets.length}
        onComplete={() => setShowEmptyGalleryGuide(false)}
      />
      
      <NFTTabTooltip
        isVisible={isNewGallery}
      />
      
      <Footer isViewMode={isViewMode} />
      
      <BrowserCompatibility />
      
      {showHelp && (
        <FAQ onClose={() => setShowHelp(false)} />
      )}
      </div>
    </ErrorBoundary>
  );
}

// Wrap app with HederaProvider and TransformProvider
function App() {
  return (
    <HederaProvider>
      <TransformProvider>
        <AppContent />
      </TransformProvider>
    </HederaProvider>
  );
}

export default App;
